'use client'

import React, { useCallback, useEffect, useState, useMemo } from 'react'
import Link from 'next/link'
import { Box, Theme, Typography, useMediaQuery, useTheme } from '@mui/material'
import { TokenContextResponse } from 'src/interfaces/invite'
import { GTM_EVENT } from 'src/interfaces/gtm'
import { useGtm, useShopLink, useVisit } from '@/hooks'
import { Avatar } from '@/components/ui/Avatar'
import { useAvatar } from '@/hooks'
import { config } from '@/config'
import { GatewayModal } from './modal'
import Head from 'next/head'
import { useSearchParams } from 'next/navigation'

import {
  ContentWrapper,
  HeaderWrapper,
  OrganizationDetails,
  ParticipantList,
  SupporterShopButton,
} from './Gateway.styled'
import { Utils } from '@/lib/utils'
import {
  BaseMidasIdLabel as MidasIdLabel,
  BaseOrganizationMidasIdInline as OrganizationMidasIdInline,
} from '@/components/ui'

type Props = TokenContextResponse

const SupporterGateway: React.FC<Props> = ({ registrant, participants, season, currentSeason, transportType }) => {
  const { seasonOrganization } = registrant
  const { organization } = seasonOrganization
  const { buildShopLink, mapToChannelType } = useShopLink()
  const { register: registerVisit, visitorId } = useVisit()
  const { sendDataToGTM } = useGtm()
  const theme = useTheme<Theme>()
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'))
  const isSmallMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('sm'))
  const isMobileLandscape = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px) and (orientation: landscape)`)
  const isCurrentSeasonLink = season.name === currentSeason.name
  const searchParams = useSearchParams()
  const [locationData, setLocationData] = useState({ href: '', origin: '' })
  const { getAvatarLink } = useAvatar()

  useEffect(() => {
    if (typeof window === 'undefined') return
    setLocationData({
      href: window.location.href,
      origin: window.location.origin,
    })
  }, [])
  useEffect(() => {
    if (!isCurrentSeasonLink) {
      sendGtmData()
      const shopLink = buildShopLink({
        participants: [
          {
            code: config.gateway.expired_sales_seller_id,
            firstName: '',
            lastName: '',
          },
        ],
        channel: 'ExLink',
      })
      window.location.replace(shopLink)
    }
  }, [])

  useEffect(() => {
    if (!visitorId || !locationData.href) {
      return
    }
    registerVisit({
      url: locationData.href,
      participants,
      midasId: organization?.midasId,
    })
  }, [visitorId, locationData.href, participants, organization?.midasId, registerVisit])

  const getParticipantsNames = useCallback(
    () => participants.map((participant) => participant.firstName).join(', '),
    [participants],
  )

  const sendGtmData = () => {
    sendDataToGTM({ event: GTM_EVENT.INVITE_GATEWAY_SHOP_NOW })
  }

  if (!organization || !participants.length || !isCurrentSeasonLink) {
    return null
  }

  const channelParam = searchParams.get('channel') || mapToChannelType(transportType)
  const utmParams = {
    utm_medium: searchParams.get('utm_medium') ?? null,
    utm_source: searchParams.get('utm_source') ?? null,
    utm_campaign: searchParams.get('utm_campaign') ?? null,
  }

  const renderGoal = () => {
    if (!seasonOrganization.goal && !+seasonOrganization.goalAmount) return null
    return (
      <>
        <Box mt={1}>
          <Typography variant="h3">
            OUR TOTAL SALES GOAL {!!+seasonOrganization.goalAmount && Utils.formatMoney(seasonOrganization.goalAmount)}
          </Typography>
        </Box>
        {!!seasonOrganization.goal && (
          <Box mt={1}>
            <Typography
              variant="body2"
              dangerouslySetInnerHTML={{
                __html: Utils.textToHTML(seasonOrganization.goal),
              }}
            />
          </Box>
        )}
      </>
    )
  }

  const avatarSize = useMemo(() => {
    if (isSmallMobile) {
      return participants.length > 2 ? 'secondary' : 'sixth'
    }
    if (isMobile && participants.length > 2) {
      return participants.length > 4 ? 'secondary' : 'sixth'
    } else if (isMobile) {
      return 'third'
    }

    return participants.length > 4 ? 'sixth' : participants.length > 3 ? 'third' : 'primary'
  }, [participants.length, isMobile, isSmallMobile])

  return (
    <Box>
      <Head>
        <meta property="og:url" content={locationData.href} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`Click here to support ${getParticipantsNames()}`} />
        <meta
          property="og:description"
          content="Discover great gifts while supporting worthy organizations. Find the highest quality products - from gift wrap, home electronics, kitchen and grilling tools to hundreds of custom embroidered and personalized items"
        />
        <meta property="og:image" content={`${locationData.origin}/gateway-modal-logo.jpg`} />
      </Head>
      <GatewayModal isVisible>
        <ContentWrapper isMobileLandscape={isMobileLandscape} sx={{ px: 0 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
              minHeight: 0,
              overflowY: 'auto',
              '& > *': { flexShrink: 0 },
            }}
          >
            <HeaderWrapper sx={{ mt: 1 }}>
              <Typography variant="h2" sx={{ fontWeight: 500, fontSize: '22px' }}>
                Every purchase supports
              </Typography>
            </HeaderWrapper>

            <ParticipantList sx={{ flexWrap: 'wrap' }}>
              {participants.map((participant) => (
                <li key={participant.id}>
                  <Box mr={1} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
                    <Avatar
                      size={avatarSize}
                      src={getAvatarLink(participant)}
                      style={{ borderRadius: '6px 6px 0 0' }}
                    />
                    <Typography variant="body1" sx={{ mt: '3px', textAlign: 'center' }}>
                      {participant.firstName} {participant.lastName[0].toUpperCase()}.
                    </Typography>
                  </Box>
                </li>
              ))}
            </ParticipantList>
            <OrganizationDetails variant="body1">
              From {organization?.name}, {organization.city}, {organization.state}
              {'\u00A0\u00A0\u00A0 '}
              <OrganizationMidasIdInline>
                <MidasIdLabel>Group&nbsp;ID:&nbsp;</MidasIdLabel>
                {organization?.midasId}
              </OrganizationMidasIdInline>
            </OrganizationDetails>
            {renderGoal()}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
            <SupporterShopButton
              fullWidth
              target="_blank"
              variant="contained"
              href={buildShopLink({
                seasonName: season.name,
                seasonOrganization,
                organization,
                participants,
                channel: channelParam,
                utmParams,
              })}
              LinkComponent={Link}
              onClick={sendGtmData}
            >
              Shop!
            </SupporterShopButton>
          </Box>
        </ContentWrapper>
      </GatewayModal>
    </Box>
  )
}

export default SupporterGateway
